import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import SideBar from "../components/SideBar";
import {
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";

export default function AllUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSideBarOpen, setIsSideBarOpen] = useState(true);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectionModel, setSelectionModel] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [statusID, setStatusID] = useState(0);
  const [reasonID, setReasonID] = useState(0);
  const [recipientCategoryID, setRecipientCategoryID] = useState(0);

  const [statusList, setStatusList] = useState([]);
  const [reasonList, setReasonList] = useState([]);
  const [recipientCategories, setRecipientCategories] = useState([]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(users);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, "shipments.xlsx");
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const userData = JSON.parse(localStorage.getItem("userData"));
        const runnerID = userData.runnerID;
        const response = await axios.get(
          `https://vsoftrunners.com-eg.net/api/RunnerUsers/V7/GetAllShipments/${runnerID}`,
          {
            headers: {
              CompanyID: "212606",
              AccessToken: userData.accessToken,
            },
          }
        );

        if (response.data.withRunnerShipments) {
          const formattedUsers = response.data.withRunnerShipments.map(
            (user, index) => ({
              ...user,
              id: index,
              awb: user.awb,
            })
          );
          setUsers(formattedUsers);
        } else {
          console.error("No withRunnerShipments found.");
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("userData");
          navigate("/");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const openStatusModal = async () => {
    const userData = JSON.parse(localStorage.getItem("userData"));

    const runnerID = userData.runnerID;
    try {
      const res = await axios.get(
        `https://vsoftrunners.com-eg.net/api/RunnerUsers/V7/GetRunnerData/${runnerID}`,
        {
          headers: {
            CompanyID: "212606",
            AccessToken: userData.accessToken,
          },
        }
      );

      const deliveryStatuses = res.data.deliveryStatuses || [];
      const customDeliveredStatus = { statusId: 5, fNameA: "تم التوصيل" };
      const combinedStatuses = [customDeliveredStatus, ...deliveryStatuses];

      setStatusList(combinedStatuses);
      setReasonList(res.data.reasons || []);
      setRecipientCategories(res.data.recipients || []);
      setOpenModal(true);
    } catch (err) {
      console.error("فشل تحميل بيانات الحالة:", err);
      alert("حدث خطأ أثناء تحميل بيانات الحالة");
    }
  };

  const handleSave = async () => {
    const selectedShipments = users.filter((u) =>
      selectionModel.includes(u.id)
    );

    const payload = {
      shipments: selectedShipments.map((s) => ({
        awb: s.awb,
        statusID,
        reasonID: statusID === 5 ? 0 : reasonID,
        lat: 0,
        lon: 0,
        jsonData: "",
        deliveryDate: new Date().toISOString(),
        recipientID: statusID === 5 ? recipientCategoryID : 0,
        recipientName: "",
        updateNotes: "",
        idno: "",
        collectedValue: s.cod,
        awBvsAWB: false,
      })),
      pickups: [],
      commLogs: [],
      locationTracks: [],
    };

    try {
      const userData = JSON.parse(localStorage.getItem("userData"));
      const runnerID = userData.runnerID;
      await axios.post(
        `https://vsoftrunners.com-eg.net/api/RunnerUsers/V7/UpdateRunnerData/${runnerID}`,
        payload,
        {
          headers: {
            CompanyID: "212606",
            AccessToken: userData.accessToken,
          },
        }
      );
      alert("تم التحديث بنجاح");
      setOpenModal(false);
      window.location.reload();
    } catch (error) {
      console.error("خطأ أثناء التحديث:", error);
      alert("فشل التحديث");
    }
  };

  const columns = [
    { field: "awb", headerName: t("AWB"), width: 120 },
    { field: "consigneeName", headerName: t("Consignee Name"), width: 150 },
    { field: "mobile", headerName: t("Mobile"), width: 150 },
    { field: "tel1", headerName: t("telephone"), width: 150 },
    { field: "cityName", headerName: t("City Name"), width: 150 },
    { field: "contents", headerName: t("contents"), width: 150 },
    {
      field: "specialInstructions",
      headerName: t("specialInstructions"),
      width: 250,
    },
    { field: "cod", headerName: t("cod"), width: 120 },
  ];

  return (
    <Box sx={{ display: "flex" }}>
      <SideBar
        open={isSideBarOpen}
        toggleOpen={() => setIsSideBarOpen(!isSideBarOpen)}
      />
      <Box
        sx={{
          height: "calc(100vh - 180px)",
          width: "95%",
          marginTop: "70px",
          marginLeft: isSideBarOpen ? "5px" : "250px",
          transition: "margin-left 0.3s",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            marginBottom: "20px",
            fontWeight: "bold",
            color: "#263150",
            marginRight: "10px",
          }}
        >
          {t("allShipment")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={exportToExcel}
          sx={{ mb: 2 }}
        >
          {t("Export to Excel")}
        </Button>
        {selectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={openStatusModal}
            sx={{ ml: 2, mb: 2 }}
          >
            تحديث الحالة
          </Button>
        )}
        {loading ? (
          <CircularProgress />
        ) : (
          <DataGrid
            rows={users}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            checkboxSelection
            disableRowSelectionOnClick
            onRowSelectionModelChange={(newSelection) =>
              setSelectionModel(newSelection)
            }
            selectionModel={selectionModel}
            sx={{
              marginRight: "10px",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "red",
                color: "#63729A",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#324c95",
                color: "white",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#e0e0e0",
              },
            }}
          />
        )}
      </Box>
      <Dialog
        open={openModal}
        onClose={() => setOpenModal(false)}
        PaperProps={{
          sx: {
            width: "500px",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle>تحديث حالة الشحنات</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>اختر الحالة</InputLabel>
            <Select
              value={statusID}
              label="اختر الحالة"
              onChange={(e) => setStatusID(Number(e.target.value))}
            >
              <MenuItem value={0} disabled>
                -- اختر الحالة --
              </MenuItem>
              {statusList.map((s) => (
                <MenuItem key={s.statusId} value={s.statusId}>
                  {s.fNameA}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {statusID === 5 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>فئة المستلم</InputLabel>
              <Select
                value={recipientCategoryID}
                label="فئة المستلم"
                onChange={(e) => setRecipientCategoryID(Number(e.target.value))}
              >
                {recipientCategories.map((cat) => (
                  <MenuItem key={cat.recipientId} value={cat.recipientId}>
                    {cat.fNameA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {statusID !== 5 && statusID !== 0 && (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>اختر السبب</InputLabel>
              <Select
                value={reasonID}
                label="اختر السبب"
                onChange={(e) => setReasonID(Number(e.target.value))}
              >
                {reasonList.map((r) => (
                  <MenuItem key={r.reasonId} value={r.reasonId}>
                    {r.fNameA}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>إلغاء</Button>
          <Button onClick={handleSave} variant="contained" disabled={!statusID}>
            حفظ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
